import React from "react";
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Typography,
} from "@mui/material";
import MuiSelect, { SelectChangeEvent } from "@mui/material/Select";

export interface SingleSelectProps {
  id: string;
  label?: string;
  value: string;
  handleChange: (value: string) => void;
  data: Array<{
    key: string;
    value: string;
  }>;
  required?: boolean;
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  size?: "small" | "medium";
  width?: number | string;
  showPlaceholder?: boolean;
  placeholderText?: string;
}

/**
 * Single Select Component (no multi-selection)
 * Shows "(Select)" when empty and "(Clear)" when a value is selected
 */
export const SingleSelect: React.FC<SingleSelectProps> = ({
  id,
  label,
  value,
  handleChange,
  data,
  required = false,
  error = false,
  errorMessage,
  disabled = false,
  size = "medium",
  width = "100%",
  showPlaceholder = true,
  placeholderText = "(Select)",
}) => {
  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    handleChange(event.target.value);
  };

  const handleClear = () => {
    handleChange("");
  };

  const isEmpty = !value || value === "";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width,
      }}
    >
      <FormControl
        sx={{ height: size === "medium" ? "3.5rem" : undefined }}
        size={size}
        error={error}
        required={required}
      >
        <InputLabel id={`${id}-label`} required={required} shrink={true}>
          {label}
        </InputLabel>
        <MuiSelect
          labelId={`${id}-label`}
          id={id}
          value={value}
          label={label}
          onChange={handleSelectChange}
          disabled={disabled}
          displayEmpty
          notched={true}
        >
          {showPlaceholder && isEmpty && (
            <MenuItem value="" disabled>
              <Typography sx={{ color: "text.secondary", fontSize: "16px" }}>
                {placeholderText}
              </Typography>
            </MenuItem>
          )}

          {!isEmpty && (
            <MenuItem value="" onClick={handleClear}>
              <Typography
                sx={{
                  fontStyle: "italic",
                  color: "text.secondary",
                  fontSize: "16px",
                }}
              >
                (Clear)
              </Typography>
            </MenuItem>
          )}

          {data.map((item) => (
            <MenuItem key={item.key} value={item.key}>
              {item.value}
            </MenuItem>
          ))}
        </MuiSelect>

        {error && errorMessage && (
          <FormHelperText error>{errorMessage}</FormHelperText>
        )}
      </FormControl>
    </Box>
  );
};
